import endpoints from "../../../../utils/endpoints";
import { CPTSlabForManualOption } from "../../../Orders/enums";
import { LOGISTICS_CPT_SLAB_LABELS_BY_LEVEL } from "../../../../constants/logisticsCptSlabs";
import type { TesterDeliveryPreference } from "../../config/types";
import type { FlowStep } from "../types";
import { FlowStepError } from "../engine/errors";
import { requireEnquiry, requireEstimate, verifyEq, waitForSnapshot } from "./common";

export const STEP_ESTIMATE_CREATE = "estimate_create";
export const STEP_ESTIMATE_CONFIGURE = "estimate_configure";
export const STEP_ESTIMATE_SHARE = "estimate_share";
export const STEP_ESTIMATE_CONFIRM = "estimate_confirm";

const idString = (value: any) =>
  value == null ? undefined : typeof value === "string" ? value : String(value._id ?? value);

const SLAB_FOR_PREFERENCE: Record<TesterDeliveryPreference, number> = {
  INSTANT: CPTSlabForManualOption.INSTANT_DELIVERY,
  STANDARD: CPTSlabForManualOption.STANDARD_DELIVERY,
  SELF_PICKUP: CPTSlabForManualOption.SELF_PICKUP,
};

const BUCKET_FOR_PREFERENCE: Record<TesterDeliveryPreference, "FASTEST" | "CHEAPEST" | null> = {
  INSTANT: "FASTEST",
  STANDARD: "CHEAPEST",
  SELF_PICKUP: null,
};

const slabLabel = (slab: number | undefined) =>
  LOGISTICS_CPT_SLAB_LABELS_BY_LEVEL[String(slab) as "1" | "2" | "-2"] ||
  (slab != null ? `cptSlab ${slab}` : "unknown");

const liveOptions = (split: any): any[] =>
  (split?.deliveryOptions || []).filter((o: any) => !o?.isDeleted);

/** Prefer the CHEAPEST (standard) / FASTEST (instant) split when dual-bucket estimates exist. */
const pickSplit = (splits: any[], supplierId: string, preference: TesterDeliveryPreference) => {
  const forSupplier = splits.filter((s) => idString(s.supplierId) === supplierId);
  const pool = forSupplier.length ? forSupplier : splits;
  const bucket = BUCKET_FOR_PREFERENCE[preference];
  if (bucket) {
    const typed = pool.find((s) => s.deliveryType === bucket);
    if (typed) return typed;
  }
  return pool[0];
};

/** Prefer Standard Delivery (cptSlab 2) unless config asks for Instant / Self Pickup. */
const pickDeliveryOption = (options: any[], preference: TesterDeliveryPreference) => {
  if (!options.length) return undefined;
  const target = SLAB_FOR_PREFERENCE[preference] ?? CPTSlabForManualOption.STANDARD_DELIVERY;
  return (
    options.find((o) => Number(o.cptSlab) === target) ||
    options.find((o) => Number(o.cptSlab) === CPTSlabForManualOption.STANDARD_DELIVERY) ||
    options[0]
  );
};

export const estimateCreateStep: FlowStep = {
  id: STEP_ESTIMATE_CREATE,
  name: "Create estimate (delivery options)",
  targetLabel: "Estimate created",
  description:
    "OrCA builds the estimate (POST /v1/estimate/:orderNo/delivery-options) and picks Standard Delivery from the returned options (or Instant / Self Pickup if configured).",
  actor: "ORCA",
  kind: "api",
  estimateMs: [3000, 12000],
  execute: async (rt) => {
    const enquiry = requireEnquiry(rt.ctx);
    const items = rt.ctx.snapshot?.enquiry?.orderItems?.length
      ? rt.ctx.snapshot.enquiry.orderItems.map((i) => ({ partId: i.partId, qty: i.orderedQuantity }))
      : rt.ctx.config.parts.map((p) => ({ partId: p.partId, qty: p.quantity }));

    const res = await rt.api<any>({
      label: "Fetch delivery options / create estimate",
      method: "POST",
      path: endpoints.fetchDeliveryOptions(enquiry.orderNo),
      body: { splits: [{ supplierId: rt.ctx.supplier.id, items }] },
    });

    const estimateNo: string | undefined = res.data?.estimateNo;
    const preference = rt.ctx.config.preferredDelivery || "STANDARD";
    const split = pickSplit(res.data?.splits || [], rt.ctx.supplier.id, preference);
    const splitId = idString(split?._id);
    const options = liveOptions(split);
    const option = pickDeliveryOption(options, preference);
    const deliveryOptionId = idString(option?._id ?? option?.id);
    const selectedVehicleType = split?.selectedVehicleType || option?.vehicleTypeCosts?.[0]?.vehicleType;

    if (!estimateNo || !splitId) {
      throw new FlowStepError("Estimate response is missing estimateNo or split id", {
        apiCall: res.record,
        expected: "estimateNo and splits[0]._id",
        actual: JSON.stringify({ estimateNo, splitId }),
      });
    }
    if (!deliveryOptionId) {
      throw new FlowStepError(
        "No delivery option was returned for the supplier split. The supplier/customer pair has no serviceable delivery option (check supplier address, customer address geocode and delivery network config).",
        { apiCall: res.record, expected: "splits[0].deliveryOptions.length > 0", actual: "0 options" }
      );
    }
    rt.ctx.estimate = { estimateNo, splitId, deliveryOptionId, selectedVehicleType: selectedVehicleType ? String(selectedVehicleType) : undefined };
    rt.log(
      `Estimate ${estimateNo} created; split ${splitId} (${split.deliveryType || "untyped"}); picked ${slabLabel(
        Number(option.cptSlab)
      )} from ${options.length} option(s)`,
      "success"
    );
  },
  verify: (s, ctx) => verifyEq("estimate.estimateNo", ctx.estimate?.estimateNo || "?", s.estimate?.estimateNo),
};

export const estimateConfigureStep: FlowStep = {
  id: STEP_ESTIMATE_CONFIGURE,
  name: "Configure estimate split",
  targetLabel: "Estimate split configured",
  description:
    "OrCA sets transactionType and payable route on the split (PATCH /v1/estimate/:no/splits/:splitId). Required before share/confirm.",
  actor: "ORCA",
  kind: "api",
  estimateMs: [500, 2500],
  execute: async (rt) => {
    const est = requireEstimate(rt.ctx);
    const { transactionType, payableBy, payableTo } = rt.ctx.config;
    await rt.api({
      label: "Patch estimate split",
      method: "PATCH",
      path: endpoints.patchEstimateSplit(est.estimateNo, est.splitId),
      body: { transactionType, payableBy, payableTo, fulfillmentStatus: "FULFILLED" },
    });
  },
  verify: (s, ctx) => {
    const ok =
      s.estimate?.transactionType === ctx.config.transactionType &&
      Boolean(s.estimate?.payableBy) &&
      Boolean(s.estimate?.payableTo);
    return {
      ok,
      expected: `split.transactionType = ${ctx.config.transactionType}, payableBy/payableTo set`,
      actual: `split.transactionType = ${s.estimate?.transactionType}, payableBy = ${s.estimate?.payableBy}, payableTo = ${s.estimate?.payableTo}`,
    };
  },
};

export const estimateShareStep: FlowStep = {
  id: STEP_ESTIMATE_SHARE,
  name: "Share estimate with customer",
  targetLabel: "Estimate shared",
  description: "OrCA shares the estimate (POST /v1/estimate/:no/share). Success of the share API is enough to continue.",
  actor: "ORCA",
  kind: "api",
  optional: true,
  estimateMs: [800, 4000],
  execute: async (rt) => {
    const est = requireEstimate(rt.ctx);
    await rt.api({
      label: "Share estimate",
      method: "POST",
      path: endpoints.shareEstimate(est.estimateNo),
      body: {},
    });
    rt.log(`Estimate ${est.estimateNo} shared`, "success");
  },
};

export const estimateConfirmStep: FlowStep = {
  id: STEP_ESTIMATE_CONFIRM,
  name: "Customer confirms estimate",
  targetLabel: "Estimate confirmed → order allocated",
  description:
    "Customer confirms the split (POST /v1/estimate/:no/splits/:splitId/confirm?mode=assign). Backend synchronously creates the child order, ACKs it and allocates the supplier.",
  actor: "CUSTOMER",
  kind: "api",
  estimateMs: [3000, 15000],
  execute: async (rt) => {
    const est = requireEstimate(rt.ctx);
    const res = await rt.api<any>({
      label: "Confirm estimate split",
      method: "POST",
      path: endpoints.confirmEstimateSplit(est.estimateNo, est.splitId, "assign"),
      body: {
        deliveryOptionId: est.deliveryOptionId,
        ...(est.selectedVehicleType ? { selectedVehicleType: est.selectedVehicleType } : {}),
      },
      actor: "CUSTOMER",
    });
    const child = res.data?.order ?? res.data;
    if (!child?._id || !child?.orderNo) {
      throw new FlowStepError("Confirm response did not include the child order", {
        apiCall: res.record,
      });
    }
    rt.ctx.child = { orderId: String(child._id), orderNo: child.orderNo };
    rt.log(
      `Child order ${child.orderNo} created (${child.orderStatus}) orderNo=${child.orderNo} _id=${child._id}`,
      "success"
    );

    await waitForSnapshot(rt, {
      trigger: "Estimate confirmation",
      expected: "Child order ALLOCATED with supplier allocation NEW",
      check: (s) => s.child?.orderStatus === "ALLOCATED",
      describe: (s) => `order=${s.child?.orderStatus}, supplier=${s.child?.supplierStatus}`,
      maxAttempts: 4,
    });
  },
  verify: (s) => verifyEq("order.orderStatus", "ALLOCATED", s.child?.orderStatus),
};
